import { useState, useContext, useEffect, useMemo } from 'react'
// OLD CODE - Commented out for migration cleanup
// import DataContext from '../../../store/DataContext';
import WizardDataContext from '../../../store/WizardDataContext';
import { usePricePlan } from '../../../store/PricePlanContext';
import { useAffiliateCode } from '../../../hooks/useAffiliateCode';
import {
  couponApiHeaders,
  isCouponTokenForbidden,
  COUPON_TOKEN_INVALID_MESSAGE,
  popinApiHeaders,
  isPopinTokenForbidden,
  POPIN_TOKEN_INVALID_MESSAGE,
  withPopinManageToken
} from '../../../utils/couponApiUtils';
import { isOnlyPlatformBaseUrl } from '../../../utils/urlUtils';
import { markPaymentPending, markEntryPaid } from '../../../utils/paymentLock';
import { usePaymentLockGuard } from '../../../hooks/usePaymentLockGuard';

import { CloseRounded } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { Divider, IconButton, Menu, MenuItem, Box, TextField } from '@mui/material';

import { useNavigate } from 'react-router-dom';

export default function Stepper() {

  const navigate = useNavigate();
  // OLD CODE - Commented out for migration cleanup
  // const dataCtx = useContext(DataContext); // Keep for backward compatibility during migration
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const { selectedPlan } = usePricePlan();
  
  // Map wizardData to dataCtx structure for Resume component
  const company = wizardData.company;
  const logo = wizardData.logo;
  const couponType = wizardData.couponType;
  const expiration = wizardData.expiration;
  const couponDetails = wizardData.couponDetails;
  const terms = wizardData.terms;
  const locations = wizardData.locations;
  const categories = wizardData.categories;
  const isPopin = wizardData.dealSelection?.selectedDeal === 'popin';
  const includeCoupon = wizardData.dealSelection?.includeCoupon ?? true;
  const isPopinWithoutCoupon = isPopin && includeCoupon === false;

  const [popinFromApi, setPopinFromApi] = useState(null);

  // affiliate code hook
  const { affiliateCode, affiliateCodeValid, affiliateCodeLocked, affiliateCodeError, handleAffiliateCodeChange, isVerifying } = useAffiliateCode();

  // Keep meta in sync with current affiliate (from hook/localStorage) so confirm sends it and it persists in wizardData
  useEffect(() => {
    const extractCode = (input) => {
      if (!input || typeof input !== 'string') return '';
      const trimmed = input.trim();
      const urlMatch = trimmed.match(/advertise\.delivait\.com\/([^\/\s]+)/i);
      if (urlMatch) return urlMatch[1].toUpperCase();
      return trimmed.toUpperCase();
    };
    const code = affiliateCode ? extractCode(affiliateCode) : '';
    if (affiliateCodeValid && code && wizardData.meta?.affiliateCode !== code) {
      updateData('meta', { affiliateCode: code });
    }
  }, [affiliateCode, affiliateCodeValid, wizardData.meta?.affiliateCode, updateData]);

  const [companyId, setCompanyId] = useState(null)
  const [couponId, setCouponId] = useState(null)
  const [returnTo, setReturnTo] = useState(null);

  const [editStep, setEditStep] = useState(1)
  const [menuState, setMenuState] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false)
  // const [openMenu, setOpenMenu] = useState(false);
  const openMenu = Boolean(menuState)

  // Utility function to determine if a color is light or dark
  const isLightColor = (color) => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  // Get text color based on background
  const getTextColor = (backgroundColor) => {
    return isLightColor(backgroundColor) ? '#000000' : '#ffffff';
  };

  useEffect(() => {
    const search = window.location.search;
    const query = new URLSearchParams(search);
    const companyIdParam = query.get('companyId');
    const couponIdParam = query.get('couponId');
    const nextCompanyId = companyIdParam != null ? Number.parseInt(companyIdParam, 10) : null;
    const nextCouponId = couponIdParam != null ? Number.parseInt(couponIdParam, 10) : null;

    setCompanyId(Number.isFinite(nextCompanyId) ? nextCompanyId : null);
    setCouponId(Number.isFinite(nextCouponId) ? nextCouponId : null);

    if (Number.isFinite(nextCompanyId)) {
      updateData('meta', { companyId: nextCompanyId });
    }
    if (Number.isFinite(nextCouponId)) {
      updateData('meta', { couponId: nextCouponId });
    }
    const pid = query.get('popinId');
    if (pid) {
      updateData('meta', { popinId: +pid });
    }
    const rt = query.get('returnTo');
    if (rt) setReturnTo(rt);
    const manageTok = query.get('t');
    if (manageTok) {
      updateData('meta', { popinManageToken: manageTok });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL into meta once on load
  }, []);

  const isRenewEditMode = !!returnTo;

  // Block re-entry into an already-paid entry (Back button / bfcache). Skipped in
  // renew edit mode, which intentionally edits a previously-paid pop-in.
  usePaymentLockGuard({
    couponId: couponId ?? wizardData.meta?.couponId,
    popinId: wizardData.meta?.popinId,
    enabled: !isRenewEditMode
  });

  useEffect(() => {
    if (!isPopinWithoutCoupon) return;
    const pid = wizardData.meta?.popinId;
    const tok = wizardData.meta?.popinCreatorToken;
    const manageTok = wizardData.meta?.popinManageToken;
    if (!pid || (!tok && !manageTok) || !process.env.REACT_APP_API_URI) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/popin/${pid}`, {
      headers: tok ? popinApiHeaders(tok) : withPopinManageToken({}, manageTok)
    })
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d.status === 'success' && d.data) setPopinFromApi(d.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isPopinWithoutCoupon, wizardData.meta?.popinId, wizardData.meta?.popinCreatorToken, wizardData.meta?.popinManageToken]);

  const redirectToStep = (step) => {
    // Step numbers: 1=company, 2=logo, 3=deal-selection, 4=coupon-type, 5=expiration,
    // 6=coupon-details, 7=terms, 8=instructions, 9=locations, 10=categories
    const pid = wizardData.meta?.popinId;
    const popinQs = pid ? `&popinId=${pid}` : '';
    navigate(
      `/advertise/coupon/short-term/create-coupon/${step}?edit=true&companyId=${companyId}&couponId=${couponId}${popinQs}`
    );
  };

  const handleCloseMenu = () => {
    setMenuState(null);
  }

  const handleClick = (event) => {
    setMenuState(event.currentTarget);
  };

  const handleTokenInvalid = () => {
    wizardCtx.clearAllData();
    alert(COUPON_TOKEN_INVALID_MESSAGE);
    navigate('/advertise/coupon/short-term/create-coupon/1?edit=false');
  };

  const handleColorChange = async (color) => {
    // Update local state immediately for responsive UI
    wizardCtx.updateData('company', { backgroundColor: color });
    
    // Update via API
    try {
      const api = process.env.REACT_APP_API_URI;
      if (!api) return;

      const popinId = wizardData.meta?.popinId;
      const popinCreatorToken = wizardData.meta?.popinCreatorToken;
      const popinManageToken = wizardData.meta?.popinManageToken;

      const isPopinDeal = wizardData.dealSelection?.selectedDeal === 'popin';
      const shouldUsePopinCompanyUpdate = isPopinDeal && popinId && (popinCreatorToken || popinManageToken);

      const url = shouldUsePopinCompanyUpdate
        ? `${api}/v3/advertising/popin/${popinId}/company`
        : `${api}/v3/advertising/step/${couponId || wizardData.meta.couponId}/company`;

      const headers = shouldUsePopinCompanyUpdate
        ? (popinCreatorToken
          ? popinApiHeaders(popinCreatorToken, { 'Content-Type': 'application/json' })
          : withPopinManageToken({ 'Content-Type': 'application/json' }, popinManageToken))
        : couponApiHeaders(wizardData.meta.creatorToken, { 'Content-Type': 'application/json' });

      if (!shouldUsePopinCompanyUpdate && !(couponId || wizardData.meta.couponId)) {
        console.warn('No couponId available to update background color');
        return;
      }

      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ backgroundColor: color })
      });

      const data = await response.json();

      if (shouldUsePopinCompanyUpdate) {
        if (isPopinTokenForbidden(response, data)) {
          wizardCtx.clearAllData();
          alert(POPIN_TOKEN_INVALID_MESSAGE);
          navigate('/advertise/coupon/short-term/create-coupon/1?edit=false');
          return;
        }
      } else {
        if (isCouponTokenForbidden(response, data)) {
          handleTokenInvalid();
          return;
        }
      }
      
      if (data.status === 'success') {
        console.log('Background color updated successfully');
      } else {
        console.error('Failed to update background color:', data.error || data.message);
      }
    } catch (error) {
      console.error('Error updating background color:', error);
    }
  };

  const handleBackToRenew = () => {
    if (!returnTo) return;
    navigate(returnTo);
  };


  const companySummaryDisplay = useMemo(() => {
    const backgroundColor = company.backgroundColor || '#FFFFFF';
    const textColor = getTextColor(backgroundColor);

    // Build socials list: only show if user added something beyond the platform base URL
    const socials = [];
    if (company.facebookPage && !isOnlyPlatformBaseUrl(company.facebookPage, 'facebook')) {
      socials.push({ key: 'facebook', label: 'Facebook', value: company.facebookPage });
    }
    if (company.instagramProfile && !isOnlyPlatformBaseUrl(company.instagramProfile, 'instagram')) {
      socials.push({ key: 'instagram', label: 'Instagram', value: company.instagramProfile });
    }
    if (company.pinterestProfile && !isOnlyPlatformBaseUrl(company.pinterestProfile, 'pinterest')) {
      socials.push({ key: 'pinterest', label: 'Pinterest', value: company.pinterestProfile });
    }

    // Alternate socials between right and left columns:
    // 1st -> right, 2nd -> left, 3rd -> right, etc.
    const leftSocials = [];
    const rightSocials = [];
    socials.forEach((social, index) => {
      if (index % 2 === 0) {
        rightSocials.push(social);
      } else {
        leftSocials.push(social);
      }
    });
    
    return (
      <div 
        className="company-summary"
        style={{
          backgroundColor: backgroundColor,
          color: textColor
        }}
      >
        {/* Menu Icon */}
        <div className="edit edit-company-summary">
          <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(0); handleClick(e); }} >
            <MoreVertIcon style={{ color: textColor }} />
          </IconButton>
          <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 0} onClose={handleCloseMenu} >
            <MenuItem onClick={() => navigate(`/advertise/coupon/short-term/create-coupon/2?edit=true&companyId=${companyId}&couponId=${couponId}`)}>
              EDIT LOGO
            </MenuItem>
            <MenuItem onClick={() => navigate(`/advertise/coupon/short-term/create-coupon/1?edit=true&companyId=${companyId}&couponId=${couponId}`)}>
              EDIT COMPANY DETAILS
            </MenuItem>
          </Menu>
        </div>

        {/* Page Title + Subtitle */}
        <div className="resume-header-text" style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ margin: 0, color: textColor }}>
            Review Your Promotional Offer
          </h1>
          <p style={{ margin: '8px 0 0', color: textColor, opacity: 0.8 }}>
            Carefully Review The Details Of Your Offer
          </p>
        </div>

        {/* Company Logo - Centered */}
        <div className="company-logo-container">
          {company.companyLogo || logo.url ? (
            <img 
              src={company.companyLogo || logo.url} 
              alt="Company Logo" 
              style={{ maxHeight: '160px', maxWidth: '400px' }}
            />
          ) : (
            <div className="logo-fallback" style={{ height: '160px', width: '160px', fontSize: '48px' }}>
              {company.name?.charAt(0)?.toUpperCase() || 'C'}
            </div>
          )}
        </div>

        {/* Color Picker Button */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <Box
            component="label"
            htmlFor="background-color-picker"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('background-color-picker')?.click();
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: `2px solid ${textColor}`,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              color: textColor,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: backgroundColor,
                border: `2px solid ${textColor}`,
                display: 'inline-block',
              }}
            />
            <span className="resume-color-picker-label">Change Background Color</span>
          </Box>
          <input
            id="background-color-picker"
            type="color"
            value={backgroundColor}
            onChange={(e) => handleColorChange(e.target.value)}
            style={{ 
              position: 'absolute',
              opacity: 0,
              width: 0,
              height: 0,
              overflow: 'hidden'
            }}
          />
        </div>

        {/* Company Name */}
        <div className="company-name">
          <h1 style={{ color: textColor }}>
            {company.name}
          </h1>
        </div>

        {/* Company Details */}
        <div className="company-details">
          {/* Left Column */}
          <div>
            <div className="detail-item">
              <div className="detail-label">
                Contact Person
              </div>
              <div className="detail-value">
                {company.managerName}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                Email Address
              </div>
              <div className="detail-value">
                {company.email}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                Phone Number
              </div>
              <div className="detail-value">
                {company.managerNumber}
              </div>
            </div>

            {leftSocials.map((social) => (
              <div className="detail-item" key={social.key}>
                <div className="detail-label">
                  {social.label}
                </div>
                <div className="detail-value">
                  {social.value}
                </div>
              </div>
            ))}
          </div>

          {/* Right Column */}
          <div>
            <div className="detail-item">
              <div className="detail-label">
                Company Website
              </div>
              <div className="detail-value">
                {company.website}
              </div>
            </div>

            {company.description && (
              <div className="detail-item">
                <div className="detail-label">
                  Company Description
                </div>
                <div className="detail-value">
                  {company.description.length > 50
                    ? `${company.description.slice(0, 50).trim()}...`
                    : company.description}
                </div>
              </div>
            )}

            {rightSocials.map((social) => (
              <div className="detail-item" key={social.key}>
                <div className="detail-label">
                  {social.label}
                </div>
                <div className="detail-value">
                  {social.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
          );
    }, [company, logo, getTextColor, openMenu, editStep, menuState, companyId, couponId, navigate, handleClick, handleCloseMenu]);

  const handleConfirm = async () => {
    setIsProcessing(true)
    try {
      // Extract affiliate code properly (handle URL format or plain code)
      const extractAffiliateCode = (input) => {
        if (!input || typeof input !== 'string') return '';
        const trimmed = input.trim();
        const urlMatch = trimmed.match(/advertise\.delivait\.com\/([^\/\s]+)/i);
        if (urlMatch) {
          return urlMatch[1].toUpperCase();
        }
        return trimmed.toUpperCase();
      };

      // Use meta (persisted with wizard) first, then fall back to hook so we send the affiliate we have for this coupon
      const metaCode = wizardData.meta?.affiliateCode;
      const extractedAffiliateCode = metaCode || (affiliateCode ? extractAffiliateCode(affiliateCode) : '');

      // Prepare payment fields and affiliate code for the API
      const paymentData = {
        customerEmail: company?.email || '',
        customerName: company?.name || ''
      };

      if (extractedAffiliateCode && (affiliateCodeValid || metaCode)) {
        paymentData.affiliateCode = extractedAffiliateCode;
      }

      const applyConfirmSuccess = (data) => {
        if (!data.data?.requiresPayment) {
          try {
            localStorage.removeItem('deliva_affiliate_code');
            updateData('meta', { affiliateCode: null });
          } catch (e) {
            console.warn('Could not clear affiliate after confirm', e);
          }
        }
        if (data.data?.requiresPayment) {
          // Remember which entry is being paid so, once the user lands on the
          // success page, we can lock it against further edits / re-payment.
          markPaymentPending({
            couponId: wizardData.meta?.couponId || couponId,
            popinId: wizardData.meta?.popinId
          });
          window.location.href = data.data.paymentUrl;
        } else {
          console.log('Payment not required');
          setIsProcessing(false);
        }
      };

      // Backend rejects a second payment on an already-paid entry. Treat that as
      // "already done": lock it and send the user to the success page rather than
      // showing a confusing error after they edited a paid pop-in.
      const isAlreadyPaidError = (data) => {
        const msg = (data?.error || data?.message || '').toLowerCase();
        // Covers coupon and pop-in wording: "already made payment", "already been paid", etc.
        return msg.includes('already') && (msg.includes('payment') || msg.includes('paid'));
      };
      const handleAlreadyPaid = () => {
        markEntryPaid({
          couponId: wizardData.meta?.couponId || couponId,
          popinId: wizardData.meta?.popinId
        });
        navigate('/payment/success', { replace: true });
      };

      const popinIdConfirm = wizardData.meta.popinId;
      const popinTok = wizardData.meta.popinCreatorToken;
      if (isPopin && popinIdConfirm && popinTok) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URI}/v3/advertising/popin/${popinIdConfirm}/confirm`,
          {
            method: 'POST',
            headers: popinApiHeaders(popinTok, { 'Content-Type': 'application/json' }),
            body: JSON.stringify(paymentData)
          }
        );
        const data = await response.json();
        if (isPopinTokenForbidden(response, data)) {
          wizardCtx.clearAllData();
          alert(POPIN_TOKEN_INVALID_MESSAGE);
          navigate('/advertise/coupon/short-term/create-coupon/1?edit=false');
          setIsProcessing(false);
          return;
        }
        if (data.status === 'success') {
          applyConfirmSuccess(data);
        } else if (isAlreadyPaidError(data)) {
          handleAlreadyPaid();
        } else {
          const errorParams = new URLSearchParams({
            error_code: data.code?.toString() || 'UNKNOWN_ERROR',
            error_message: data.error || 'An error occurred',
            error_details: data.details || ''
          });
          if (data.flagged_categories && Array.isArray(data.flagged_categories)) {
            errorParams.set('flagged_categories', data.flagged_categories.join(','));
          }
          navigate(`/payment/cancel?${errorParams.toString()}`);
        }
        return;
      }

      const creatorToken = wizardData.meta.creatorToken;
      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/v3/advertising/step/${wizardData.meta.couponId || couponId}/confirm`,
        {
          method: 'POST',
          headers: couponApiHeaders(creatorToken, { 'Content-Type': 'application/json' }),
          body: JSON.stringify(paymentData)
        }
      );

      const data = await response.json();

      if (isCouponTokenForbidden(response, data)) {
        handleTokenInvalid();
        setIsProcessing(false);
        return;
      }

      if (data.status === 'success') {
        applyConfirmSuccess(data);
      } else if (isAlreadyPaidError(data)) {
        handleAlreadyPaid();
      } else {
        const errorParams = new URLSearchParams({
          error_code: data.code?.toString() || 'UNKNOWN_ERROR',
          error_message: data.error || 'An error occurred',
          error_details: data.details || ''
        });

        if (data.flagged_categories && Array.isArray(data.flagged_categories)) {
          errorParams.set('flagged_categories', data.flagged_categories.join(','));
        }

        navigate(`/payment/cancel?${errorParams.toString()}`);
      }
    } catch (error) {
      console.error('Error confirming coupon:', error);
      // Redirect to error page with network error
      const errorParams = new URLSearchParams({
        error_code: 'NETWORK_ERROR',
        error_message: 'Network error occurred',
        error_details: error.message || 'Please check your connection and try again'
      });
      navigate(`/payment/cancel?${errorParams.toString()}`);
    }
  };

  const popinVisitorsDisplay = popinFromApi?.visitors ?? wizardData.popinBudget?.visitors;
  const popinPriceDisplay = popinFromApi?.price ?? wizardData.popinBudget?.price;

  const locationsSection = (
    <div className="coupon-location-container">
      <div className="resume-inner">
        <div className="main-title full-width">
          <h1>Locations</h1>

          <div className="edit">
            <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(8); handleClick(e); }} >
              <MoreVertIcon />
            </IconButton>
            <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 8} onClose={handleCloseMenu} >
              <MenuItem onClick={() => redirectToStep(9)}>
                EDIT
              </MenuItem>
            </Menu>
          </div>

        </div>
        <div className="resume-location full-width">
          <div className="locations">
            {
              locations.isOnlineOnly ? (
                <div className="online-only-info">
                  {locations.isGlobal ? (
                    <div className="global-message">
                      This coupon is available online worldwide
                    </div>
                  ) : (
                    <div className="countries-message">
                      <div className="message-title">This coupon is available online only in countries:</div>
                      <div className="countries-list">
                        {locations.countries?.map((country) => (
                          <span key={country.id} className="country-item">
                            {country.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                locations.list && locations.list.length > 0
                  ? locations.list.map((item, index) => (
                      <div className="location" key={index}>
                        <div className="location-number">{String(index + 1).padStart(2, '0')}</div>
                        <div className="location-text">
                          <span className='location-title'>{item.title}</span>
                          <span className='location-address'>{item.address}</span>
                        </div>
                      </div>
                    ))
                  : <div className="no-locations">
                      No locations added
                    </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );

  if (isPopinWithoutCoupon) {
    return (
      <>
        <div className="resume-container">
          <div className="resume-inner">{companySummaryDisplay}</div>

          <div className="coupon-coupon-container-2">
            <div className="resume-inner">
              <div className="coupon-card-container coupon-card-container-flex">
                <div className="coupon-details">
                  <div className="edit edit-coupon-container">
                    <IconButton
                      aria-label="more"
                      id="long-button"
                      aria-controls={openMenu ? 'long-menu' : undefined}
                      aria-expanded={openMenu ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={(e) => {
                        setEditStep(10);
                        handleClick(e);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      id="long-menu"
                      MenuListProps={{ 'aria-labelledby': 'long-button' }}
                      anchorEl={menuState}
                      open={openMenu && editStep === 10}
                      onClose={handleCloseMenu}
                    >
                      <MenuItem onClick={() => redirectToStep(5)}>EDIT</MenuItem>
                    </Menu>
                  </div>
                  <div className="coupon-left">
                    <h1 className="medium-font">
                      {popinVisitorsDisplay != null ? popinVisitorsDisplay : '—'}
                    </h1>
                    <h3 className="small-font">visitors</h3>
                    <span>Pop-In reach</span>
                  </div>

                  <Divider orientation="vertical" />

                  <div className="coupon-right">
                    <h1>
                      {popinPriceDisplay != null ? `${popinPriceDisplay} rands` : '—'}
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {locationsSection}

          <div className="resume-inner">
            <div
              className="affiliate-code-container"
              style={{
                marginBottom: '30px',
                padding: '20px',
                maxWidth: '600px',
                margin: '0 auto 30px auto'
              }}
            >
              <TextField
                label="Affiliate Code"
                value={affiliateCode}
                variant="standard"
                onChange={handleAffiliateCodeChange}
                InputProps={{ readOnly: affiliateCodeLocked }}
                inputProps={affiliateCodeLocked ? { tabIndex: -1 } : {}}
                sx={
                  affiliateCodeLocked
                    ? {
                        '& .MuiInput-underline.Mui-focused:after': { borderBottomColor: 'transparent' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottomColor: 'rgba(0,0,0,0.42)'
                        },
                        '& .MuiFormLabel-root.Mui-focused': { color: 'rgba(0,0,0,0.6)' },
                        '& input': { cursor: 'default' }
                      }
                    : {}
                }
                error={affiliateCodeError !== null && !affiliateCodeValid}
                helperText={
                  isVerifying
                    ? 'Verifying...'
                    : affiliateCodeError && !affiliateCodeValid
                      ? affiliateCodeError
                      : affiliateCodeLocked
                        ? 'Affiliate code verified and locked'
                        : affiliateCodeValid
                          ? 'Affiliate code verified'
                          : 'Optional: Enter your affiliate code'
                }
                fullWidth
                style={{ marginTop: '10px' }}
              />
            </div>
          </div>

          <div className="resume-inner">
            <div className="pay-container">
              <div className="pay">
                <div
                  className="pay-button"
                  onClick={handleConfirm}
                  style={{
                    opacity: isProcessing ? 0.7 : 1,
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    pointerEvents: isProcessing ? 'none' : 'auto'
                  }}
                >
                  {isProcessing ? (
                    <div
                      className="spinner"
                      style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #ffffff33',
                        borderTop: '2px solid #ffffff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto'
                      }}
                    />
                  ) : (
                    <>Confirm & Pay</>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="deliva-logo">
          <div className="logo" />
          <div className="close-coupon">
            <CloseRounded />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="resume-container">
        <div className="resume-inner">
          {companySummaryDisplay}
        </div>

          <div className="coupon-coupon-container-2">
            <div className="resume-inner">
            <div className="coupon-card-container coupon-card-container-margin">
            <div className="coupon-desc-wrapper">
                {couponDetails.description && (
                  <div className="coupon-desc">
                    <div className="edit edit-coupon-container">
                      <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(5); handleClick(e); }} >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 5} onClose={handleCloseMenu} >
                        <MenuItem onClick={() => redirectToStep(6)}>
                          EDIT
                        </MenuItem>
                      </Menu>
                    </div>
                    <h1>Coupon Description</h1>
                    <h3>{couponDetails.description}</h3>
                  </div>
                )}
              </div>
            </div>
            
            <div className="coupon-card-container coupon-card-container-margin">
              <div className="coupon-info-container">
                {couponDetails.website && (
                  <div className="coupon-info-box">
                    <div className="edit edit-coupon-container">
                      <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(5); handleClick(e); }} >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 5} onClose={handleCloseMenu} >
                        <MenuItem onClick={() => redirectToStep(6)}>
                          EDIT
                        </MenuItem>
                      </Menu>
                    </div>
                    <h1>Coupon Website</h1>
                    <h3>{couponDetails.website}</h3>
                  </div>
                )}
                {couponDetails.phone && (
                  <div className="coupon-info-box">
                    {!couponDetails.website && (
                      <div className="edit edit-coupon-container">
                        <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(5); handleClick(e); }} >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 5} onClose={handleCloseMenu} >
                          <MenuItem onClick={() => redirectToStep(6)}>
                            Edit
                          </MenuItem>
                        </Menu>
                      </div>
                    )}
                    <h1>Coupon Phone</h1>
                    <h3>{couponDetails.phone}</h3>
                  </div>
                )}
                {couponDetails.email && (
                  <div className="coupon-info-box">
                    {!couponDetails.website && !couponDetails.phone && (
                      <div className="edit edit-coupon-container">
                        <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(5); handleClick(e); }} >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 5} onClose={handleCloseMenu} >
                          <MenuItem onClick={() => redirectToStep(6)}>
                            EDIT
                          </MenuItem>
                        </Menu>
                      </div>
                    )}
                    <h1>Coupon Email</h1>
                    <h3>{couponDetails.email}</h3>
                  </div>
                )}
              </div>
            </div>

          <div className="coupon-cards-wrapper">
            <div className="coupon-card-container coupon-card-container-flex">
            <div className="coupon">
              <div className="edit edit-coupon-container">
                <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(3); handleClick(e); }} >
                  <MoreVertIcon />
                </IconButton>
                <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 3} onClose={handleCloseMenu} >
                  {!isPopinWithoutCoupon && (
                    <MenuItem onClick={() => redirectToStep(4)}>
                      EDIT
                    </MenuItem>
                  )}
                </Menu>
              </div>
              <div className="coupon-left">
                <h1 className="small-font">
                  {couponType.activeTab === 1 && `${couponType.deal} ${couponType.dealItem} Free`}
                  {couponType.activeTab === 2 && `${couponType.deal}% Off`}
                  {couponType.activeTab === 3 && `-${couponType.deal} ${couponType.currency}`}
                </h1>
              </div>
              <Divider orientation="vertical" />
              <div className="coupon-right flex-center">
                {company.companyLogo || logo.url ? (
                  <img 
                    src={company.companyLogo || logo.url} 
                    alt="Company Logo" 
                  />
                ) : (
                  <div className="logo-fallback">
                    {company.name?.charAt(0)?.toUpperCase() || 'C'}
                  </div>
                )}
              </div>
            </div>              
            </div>

            <div className="coupon-card-container coupon-card-container-flex">
              <div className="coupon-details">
                <div className="edit edit-coupon-container">
                  <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(4); handleClick(e); }} >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 4} onClose={handleCloseMenu} >
                    <MenuItem onClick={() => redirectToStep(5)}>
                      EDIT
                    </MenuItem>
                  </Menu>
                </div>
                <div className="coupon-left">
                  {isPopin ? (
                    <>
                      <h1 className="medium-font">
                        {popinVisitorsDisplay != null ? popinVisitorsDisplay : '—'}
                      </h1>
                      <h3 className="small-font">visitors</h3>
                      <span>Pop-In reach</span>
                    </>
                  ) : (
                    <>
                      <h1 className="medium-font">
                        {expiration.duration}
                      </h1>
                      <h3 className="small-font">
                        {expiration.durationType}
                      </h3>
                      <span>{selectedPlan.name}</span>
                    </>
                  )}
                </div>

                <Divider orientation="vertical" />

                <div className="coupon-right">
                  <h1>
                    {isPopin
                      ? (popinPriceDisplay != null ? `${popinPriceDisplay} rands` : '—')
                      : `${expiration.cost} rands`}
                  </h1>
                </div>
              </div>
            </div>
          </div>

        </div>
        </div>

        <div className="coupon-terms-container">
          <div className="resume-inner">
            <div className="main-title full-width">
              <h1>Terms & Conditions</h1>

              <div className="edit">
                <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(6); handleClick(e); }} >
                  <MoreVertIcon />
                </IconButton>
                <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 6} onClose={handleCloseMenu} >
                  {!isPopinWithoutCoupon && (
                    <MenuItem onClick={() => redirectToStep(7)}>
                      EDIT
                    </MenuItem>
                  )}
                </Menu>
              </div>

            </div>
            <div className="resume-terms full-width">
              <div className="terms">
                {
                  terms.terms?.map((term, i) => (
                    <div className="term" key={i}>
                      <div className="term-number">{String(i + 1).padStart(2, '0')}</div>
                      <div className="term-text">{term}</div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>

        {/* Instructions Section */}
        <div className="coupon-instructions-container">
          <div className="resume-inner">
            <div className="main-title full-width">
              <h1>Instructions</h1>

              <div className="edit">
                <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(7); handleClick(e); }} >
                  <MoreVertIcon />
                </IconButton>
                <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 7} onClose={handleCloseMenu} >
                  {!isPopinWithoutCoupon && (
                    <MenuItem onClick={() => redirectToStep(8)}>
                      EDIT
                    </MenuItem>
                  )}
                </Menu>
              </div>
            </div>
            <div className="resume-instructions full-width">
              <div className="instructions">
                {
                  terms.instructions?.map((instruction, i) => (
                    <div className="instruction" key={i}>
                      <div className="instruction-number">{String(i + 1).padStart(2, '0')}</div>
                      <div className="instruction-text">{instruction}</div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>

        <div className="coupon-website-code-container">
          <div className="resume-inner">
          <div className="website-code-wrapper">
            <div className="coupon-website-container website-code-container">
              <div className="edit edit-coupon-container">
                <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(5); handleClick(e); }} >
                  <MoreVertIcon />
                </IconButton>
                <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 5} onClose={handleCloseMenu} >
                  {!isPopinWithoutCoupon && (
                    <MenuItem onClick={() => redirectToStep(6)}>
                      Edit
                    </MenuItem>
                  )}
                </Menu>
              </div>

              <div className="coupon-website">
                <h1>Coupon Website</h1>
                <h3>{couponDetails.website}</h3>
              </div>
            </div>

            <div className="coupon-code-container website-code-container">
              <div className="edit edit-coupon-container">
                <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(5); handleClick(e); }} >
                  <MoreVertIcon />
                </IconButton>
                <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 5} onClose={handleCloseMenu} >
                  {!isPopinWithoutCoupon && (
                    <MenuItem onClick={() => redirectToStep(6)}>
                      Edit
                    </MenuItem>
                  )}
                </Menu>
              </div>

              <div className="coupon-code">
                <h1>Coupon Code</h1>
                <h1>{couponDetails.code}</h1>
              </div>
            </div>
          </div>
          </div>
        </div>


        {locationsSection}

        {!isPopinWithoutCoupon && (
          <div className="coupon-categories-container">
            <div className="resume-inner">
            <div className="coupon-categorie">
              <div className="coupon-categories">
                <div className="edit edit-coupon-container">
                  <IconButton aria-label="more" id="long-button" aria-controls={openMenu ? 'long-menu' : undefined} aria-expanded={openMenu ? 'true' : undefined} aria-haspopup="true" onClick={(e) => { setEditStep(9); handleClick(e); }} >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button', }} anchorEl={menuState} open={openMenu && editStep === 9} onClose={handleCloseMenu} >
                    <MenuItem onClick={() => redirectToStep(10)}>
                      EDIT
                    </MenuItem>
                  </Menu>
                </div>
                <div className="title">Categories</div>

                <div className="categories">
                  {
                    categories.selected && categories.selected.length > 0 ? (
                      <div className="categories-banner">
                        <div className="categories-message">
                          This coupon is available in the following categories:
                        </div>
                        <div className="categories-list">
                          {categories.selected.sort((a, b) => a.title.localeCompare(b.title)).map((category, index) => (
                            <span key={category.id} className="category-item">
                              {category.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="categories-banner">
                        <div className="categories-message">
                          No categories selected
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
            </div>

          </div>
        )}

        {/* Affiliate Code Section */}
        <div className="resume-inner">
          <div className="affiliate-code-container" style={{ 
            marginBottom: '30px', 
            padding: '20px',
            maxWidth: '600px',
            margin: '0 auto 30px auto'
          }}>
            <TextField
              label="Affiliate Code"
              value={affiliateCode} 
              variant="standard" 
              onChange={handleAffiliateCodeChange} 
              InputProps={{ readOnly: affiliateCodeLocked }}
              inputProps={affiliateCodeLocked ? { tabIndex: -1 } : {}}
              sx={affiliateCodeLocked ? {
                '& .MuiInput-underline.Mui-focused:after': { borderBottomColor: 'transparent' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(0,0,0,0.42)' },
                '& .MuiFormLabel-root.Mui-focused': { color: 'rgba(0,0,0,0.6)' },
                '& input': { cursor: 'default' },
              } : {}}
              error={affiliateCodeError !== null && !affiliateCodeValid}
              helperText={
                isVerifying 
                  ? "Verifying..." 
                  : affiliateCodeError && !affiliateCodeValid 
                    ? affiliateCodeError 
                    : affiliateCodeLocked
                      ? "Affiliate code verified and locked"
                    : affiliateCodeValid 
                      ? "Affiliate code verified" 
                      : "Optional: Enter your affiliate code"
              }
              fullWidth
              style={{ marginTop: "10px" }}
            />
          </div>
        </div>

        <div className="resume-inner">
          <div className="pay-container">
            <div className="pay">
              {isRenewEditMode ? (
                <div className="pay-button" onClick={handleBackToRenew}>
                  Back to renew
                </div>
              ) : (
                <div
                  className="pay-button"
                  onClick={handleConfirm}
                  style={{
                    opacity: isProcessing ? 0.7 : 1,
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    pointerEvents: isProcessing ? 'none' : 'auto'
                  }}
                >
                  {isProcessing ? (
                    <div
                      className="spinner"
                      style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #ffffff33',
                        borderTop: '2px solid #ffffff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto'
                      }}
                    />
                  ) : (
                    <>Confirm & Pay</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>


      </div>

      <div className="deliva-logo">
        <div className="logo"></div>
        <div className="close-coupon"><CloseRounded /></div>
      </div>

    </>

  )
}